import { create } from 'zustand';
import { es } from './es';
import { hi } from './hi';

/**
 * Translations. The English text is the key: t('Sign in') is looked up in the chosen language's dictionary, and anything not
 * translated yet stays in English, so adding a language never breaks a screen. Values may use {name} placeholders.
 *
 * The language is kept on this device (not in the synced preferences), so the sign-in screen can use it before anyone signs in.
 */
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'hi', name: 'हिन्दी' },
] as const;
export type Language = (typeof LANGUAGES)[number]['code'];

export const DICTIONARIES: Record<Language, Record<string, string>> = { en: {}, es, hi };

const KEY = 'adiox.language';
const isLanguage = (value: unknown): value is Language => LANGUAGES.some((l) => l.code === value);

/** The saved choice, else the browser's language when we have it, else English. */
export function initialLanguage(): Language {
  try {
    const saved = localStorage.getItem(KEY);
    if (isLanguage(saved)) return saved;
  } catch {
    // private mode
  }
  const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2).toLowerCase() : 'en';
  return isLanguage(browser) ? browser : 'en';
}

/** Translates `text` into `language`, filling in {placeholders}. */
export function translate(
  language: Language,
  text: string,
  values?: Record<string, string | number>,
): string {
  const found = DICTIONARIES[language][text] ?? text;
  return values
    ? found.replace(/\{(\w+)\}/g, (all, name: string) => (name in values ? String(values[name]) : all))
    : found;
}

interface LanguageState {
  language: Language;
  setLanguage: (language: Language) => void;
}

export const useLanguageStore = create<LanguageState>((set) => ({
  language: initialLanguage(),
  setLanguage: (language) => {
    try {
      localStorage.setItem(KEY, language);
    } catch {
      // kept for this visit only
    }
    if (typeof document !== 'undefined') document.documentElement.lang = language;
    set({ language });
  },
}));

/** The translate function for the current language; components re-render when the language changes. */
export function useT(): (text: string, values?: Record<string, string | number>) => string {
  const language = useLanguageStore((s) => s.language);
  return (text, values) => translate(language, text, values);
}
